import { Controller } from '@hotwired/stimulus'
import VueBaseController from '../../vue_base'

const GENERATION_POLL_INTERVAL_MS = 5000
const GENERATION_POLL_MAX_ATTEMPTS = 240 // give up after ~20 minutes

export class IndexController extends VueBaseController {
  static get COMPONENT() { return () => import('@/vue/pages/users/ai_content/content_entities/index.vue') }
}

// Polls the content endpoint while an entity is generating in the background
// and reloads the page once generation finishes (or fails).
export class GeneratingController extends Controller {
  static targets = ['hint']

  static values = { contentEntityId: String }

  connect() {
    this.pollAttempts = 0
    this.polling = true
    this.scheduleNextCheck()
  }

  disconnect() {
    this.polling = false
    clearTimeout(this.pollTimer)
  }

  scheduleNextCheck() {
    this.pollTimer = setTimeout(() => this.checkStatus(), GENERATION_POLL_INTERVAL_MS)
  }

  // Self-scheduling instead of setInterval so a slow /content response never
  // overlaps with the next check, and a late response can't act after disconnect.
  async checkStatus() {
    if (!this.polling) return

    this.pollAttempts += 1
    if (this.pollAttempts > GENERATION_POLL_MAX_ATTEMPTS) {
      this.stopPolling()
      return
    }

    try {
      const response = await fetch(
        `/ajax/users/ai_content/content_entities/${this.contentEntityIdValue}/content`,
        { headers: { Accept: 'application/json' } }
      )
      if (!this.polling) return

      if (response.status === 401 || response.status === 403) {
        this.stopPolling()
        return
      }
      if (!response.ok) {
        this.scheduleNextCheck()
        return
      }

      const data = await response.json()
      if (!this.polling) return

      if (data.status && data.status !== 'generating') {
        window.location.reload()
        return
      }
      this.scheduleNextCheck()
    } catch {
      // transient network errors: keep polling until the attempt cap
      if (this.polling) this.scheduleNextCheck()
    }
  }

  stopPolling() {
    this.polling = false
    clearTimeout(this.pollTimer)
    if (this.hasHintTarget) {
      this.hintTarget.textContent = 'Automatic refresh stopped. Reload the page to check the latest status.'
    }
  }
}

export class NewController extends VueBaseController {
  static get COMPONENT() { return () => import('@/vue/pages/users/ai_content/content_entities/new.vue') }
}

export class ShowController extends VueBaseController {
  static get COMPONENT() { return () => import('@/vue/pages/users/ai_content/content_entities/show.vue') }

  static values = {
    contentEntityId: String,
    title: String,
    contentScore: Number,
    totalTokens: Number,
    model: String,
    locationId: String,
    locationName: String,
    locationAddress: String,
    contentTypeId: String,
    contentTypeName: String,
    orderId: String,
    orderNumber: String,
    dynamicInputs: String,
    status: String
  }

  connect() {
    super.connect({
      contentEntityId: this.contentEntityIdValue,
      title: this.titleValue,
      contentScore: this.contentScoreValue,
      totalTokens: this.totalTokensValue,
      model: this.modelValue,
      locationId: this.locationIdValue,
      locationName: this.locationNameValue,
      locationAddress: this.locationAddressValue,
      contentTypeId: this.contentTypeIdValue,
      contentTypeName: this.contentTypeNameValue,
      orderId: this.orderIdValue,
      orderNumber: this.orderNumberValue,
      dynamicInputs: this.dynamicInputsValue ? JSON.parse(this.dynamicInputsValue) : null,
      status: this.statusValue
    })
  }
}
